import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { JournalSupportResponse, UpdateJournalSupportRequest, journalSupportsApi } from "@/api";
import { JournalSupportFormValues } from "@/common/components/journal-support/JournalSupportSection.types";
import { journalSupportFormSchema } from "@/common/components/journal-support/JournalSupportSection.utils";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface Props {
  entry: JournalSupportResponse;
  onSuccess: () => void;
}

export function useJournalSupportEdit({ entry, onSuccess }: Props) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [QueryKey.JournalSupports] });

  const updateMutation = useMutation({
    mutationFn: (request: UpdateJournalSupportRequest) =>
      journalSupportsApi.updateJournalSupport({ id: entry.id, updateJournalSupportRequest: request }),
    onSuccess: () => {
      void invalidate();
      showSuccess(t("calendar.journalSupport.successUpdate"));
      onSuccess();
    },
  });

  const endMutation = useMutation({
    mutationFn: () => journalSupportsApi.endJournalSupport({ id: entry.id }),
    onSuccess: () => {
      void invalidate();
      showSuccess(t("calendar.journalSupport.successEnd"));
      onSuccess();
    },
  });

  const formik = useFormikWithDirtyForm<JournalSupportFormValues>({
    initialValues: {
      startTime: entry.startTimeUtc,
      endTime: entry.endTimeUtc,
    },
    validationSchema: journalSupportFormSchema(t),
    onSubmit: async (values: JournalSupportFormValues) => {
      await updateMutation.mutateAsync({ endTimeUtc: values.endTime });
    },
  });

  return {
    formik,
    handleEnd: () => endMutation.mutate(),
    isEnding: endMutation.isPending,
  };
}
